<script lang="ts">
import { I3pService } from '@nuxtDatakodeUtils/services/i3p.service.js'
// import { I3pEventsEnum } from '@nuxtDatakodeUtils/types/I3pEvents.enum.js'
import GeoJSON from 'ol/format/GeoJSON'
import type { Feature } from 'ol'
import type { Geometry } from 'ol/geom'
import { useCookie, useRuntimeConfig } from 'nuxt/app'
import EntityInitSection from '../../common/entity/EntityInitSection.vue'
import EntityStatus from './EntityStatus.vue'
import { Commune } from '~/models/Commune'
import { useGetOlExtent } from '~/composables/useBbox'

export default {
    name: 'IntentionEntityPanelGlobalInfo',
    components: { EntityStatus },
    extends: EntityInitSection,
    props: {
        i3pService: { type: I3pService, required: true },
    },
    emits: ['updateData', 'save', 'saveStatus', 'updateMapPosition'],
    data() {
        const config = useRuntimeConfig()
        const access_token = useCookie<string>('access_token')
        return {
            streetBanList: [],
            config,
            access_token,
        }
    },

    computed: {
        commune() {
            return this.data.commune
        },
    },
    watch: {
        commune() {
            if (this.commune?.insee) {
                this.$emit('updateData', { [`commune_insee`]: this.data.commune.insee || null })
            }
        },
    },
    mounted() {
    },
    methods: {
        async getBANAddresses(search: string | null = null) {
            const map = this.i3pService.getMap()
            const projection = map.getView().getProjection()?.code_
            const res = ref()
            if (search && search?.length > 2) {
                const url = new URL('/geocodage/search', 'https://data.geopf.fr')
                url.searchParams.append('q', search)
                url.searchParams.append('type', 'street')
                if (this.commune?.insee) {
                    url.searchParams.append('citycode', this.commune.insee)
                }
                res.value = await $fetch(url.toString())
                const format = new GeoJSON()
                this.streetBanList = format.readFeatures(res.value, {
                    dataProjection: 'EPSG:4326', // BAN API is WGS84
                    featureProjection: projection, // your map projection (Web Mercator)
                }) as Feature<Geometry>[]
            }

            // ZOOM MAP map.panTo(new L.LatLng(40.737, -73.923)); STREET & COMMUNE
        },
        async setupAddress(name: string) {
            this.$emit('updateData', { [`ban_street`]: name })
            await this.getBANAddresses(name)
            const feature = this.streetBanList.find((feature: Feature) => feature.getProperties()?.name.includes(name))
            if (feature?.getProperties()?.name === name) {
                const featureExtends = feature?.getGeometry()?.getExtent()
                this.i3pService.mapFocusBbox(featureExtends)
            }
        },
        async setupCity(event: object) {
            this.$emit('updateData', {
                [`commune`]: event,
                [`commune_insee`]: event?.insee || null,
            })
            this.streetBanList = []
            if (event.insee && !this.i3pService.currentEntity?.data.k) {
                this.$emit('updateData', {
                    [`ban_street`]: null,
                })
                const communeModel = new Commune()
                const res = await communeModel.get(event.insee)
                const originalBbox = res?.data?.bbox.bbox
                const olBbox = useGetOlExtent(originalBbox)
                this.i3pService.mapFocusBbox(olBbox)
            }
        },
    },
}
</script>

<template>
    <FormSection v-if="data" title="Informations générales" with-borders-color="primary">
        <EntityStatus
            :entity
            :data
            :translation-name
            @save-status="$emit('saveStatus', $event)"
        />

        <!-- :validation="validation?.status"
            :validation-rules REMOVED TO AVOID EDIT IN FORM -->
        <FormInput
            data-key="name"
            :validation="validation?.name"
            :data
            :validation-rules
            :translation-name
            @update-data="$emit('updateData', { [`name`]: $event })"
        />
        <FormAutocomplete
            v-if="validationRules"
            data-key="organization"
            validation-key="organization_id"
            :validation="validation?.organization_id"
            :data
            :validation-rules
            :translation-name
            @update-data="$emit('updateData', { [`organization`]: $event, [`organization_id`]: $event?.id || null })"
        />
        <!-- NEED BOTH AUTOCOMPLETE : Form need to be inited with validationRules to work. Computed doesn't refresh correctly on switch -->
        <FormAutocomplete
            v-else
            data-key="organization"
            :data
            :translation-name
        />
        <FormAutocomplete
            v-if="validationRules"
            data-key="commune"
            validation-key="commune_insee"
            :validation="validation?.commune_insee"
            :data
            :validation-rules
            :translation-name
            @update-data="setupCity($event)"
        />
        <FormAutocomplete
            v-else
            data-key="commune"
            :data
            :translation-name
        />
        <FormInput
            v-if="!validationRules"
            data-key="ban_street"
            :validation="validation?.ban_street"
            :data
            :translation-name
        />
        <template v-else>
            <label class="v-label" :class="validationRules && 'mb-1'">
                {{ $translate && $translate(`${translationName}.ban_street`) }}
            </label>
            <v-combobox
                :key="data.commune_insee"
                :value="data.ban_street"
                variant="outlined"
                density="compact"
                :items="streetBanList.map((feature: Feature) => feature.getProperties()?.name)"
                no-filter
                @update:model-value="setupAddress($event)"
            />
        </template>

        <FormSelect
            data-key="due_status"
            :data
            radio-buttons
            :validation="validation?.due_status"
            :validation-rules
            :translation-name
            @update-data="$emit('updateData', { [`due_status`]: $event })"
        />
        <FormNumber
            data-key="year"
            :validation="validation?.year"
            :data
            :validation-rules
            :translation-name
            @update-data="$emit('updateData', { [`year`]: $event })"
        />
        <FormTextarea
            data-key="info"
            :validation="validation?.info"
            :data
            :validation-rules
            :translation-name
            @update-data="$emit('updateData', { [`info`]: $event })"
        />
    </FormSection>
</template>
